import {
  Moon,
  Sun,
  AlignJustify,
  MoreHorizontal,
  Plus,
  HelpCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import RightSidebar from './RightSideBar';
import { clearMemory, getChatHistory } from '../../api/api';
import { toast } from 'react-hot-toast';
import ClearMemoryDialog from './ClearMemoryDialog';
import { FolderOpen } from 'lucide-react';
import { useSelector } from 'react-redux';
import AddToSpaceModal from './AddToSpaceModal';
import ResourcesSidebar from './ResourcesSideBar';
import SidebarSquareIcon from './SideBarSquareIcon';
import { Tooltip } from 'react-tooltip';

export default function Header({
  isDarkMode,
  toggleTheme,
  chatHistory,
  setChatHistory,
  startTour,
  isMobile,
  toggleSidebar,
  resources,
}) {
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const location = useLocation();
  const match = location.pathname.match(/\/chat\/([^/]+)/);
  const chatId = match ? match[1] : 'current';
  const report = useSelector((state) => state.report.report);
  const [highlightDots, setHighlightDots] = useState(false);
  const [isAddToSpaceOpen, setIsAddToSpaceOpen] = useState(false);
  const [isResourcesSidebarOpen, setIsResourcesSidebarOpen] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const { sessions, loading } = useSelector((state) => state.chatSessions);
  const chatFromRedux = sessions.find((c) => c.chat_session_id === chatId);
  const chatFromLocal = chatHistory.find((c) => c.id === chatId);
  const isResearchSession = location.state?.isResearchSession === true;
  const isChatRoute = location.pathname.includes('/chat');
  const hasActiveChat = Boolean(chatId && chatId !== 'current');

  const refreshChatHistory = async () => {
    if (!chatId || chatId === 'current') return;

    try {
      const historyData = await getChatHistory({
        uid: user?.id,
        chat_session_id: chatId,
        limit: 1,
        offset: 0,
      });

      const chatTitle =
        historyData?.chat_session?.title || 'Untitled Conversation';
      const hasContract = !!historyData?.chat_session?.has_contract;
      const space = historyData?.spaces?.[0] || null;

      const sessionMeta = {
        id: historyData?.chat_session_id,
        title: chatTitle,
        hasContract,
        space: space
          ? {
              id: space.space_id,
              name: space.name,
              description: space.description,
            }
          : null,
        contract: historyData?.chat_session?.contract || null,
      };

      setChatHistory((prev) => {
        const existingIndex = prev.findIndex((c) => c.id === sessionMeta.id);
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            ...sessionMeta,
          };
          return updated;
        }
        return [...prev, sessionMeta];
      });
    } catch (err) {
      console.error('Failed to refresh chat history:', err);
    }
  };
  useEffect(() => {
    setIsResourcesSidebarOpen(false);
    setIsRightSidebarOpen(false);
    setIsAddToSpaceOpen(false);
    setShowConfirm(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!hasActiveChat) {
      setIsResourcesSidebarOpen(false);
    }
  }, [hasActiveChat]);

  // 🔹 Run it every time you open a chat route
  useEffect(() => {
    if (isResearchSession) return; // 🚫 skip for deep research

    if (chatId && chatId !== 'current') {
      refreshChatHistory();
    }
  }, [chatId, isResearchSession]);

  useEffect(() => {
    if (report) {
      setHighlightDots(true);
      toast.success('Your report is ready — click the 3 dots to view it!');
    }
  }, [report]);
  const getChatTitle = () => {
    if (isResearchSession) return 'Professional Research';
    if (!chatId || chatId === 'current') return 'New Legal Insights';

    return chatFromRedux?.title || chatFromLocal?.title || '';
  };

  const shouldShowAddToSpace = () => {
    const title = getTitle();
    const chat = chatHistory.find((c) => c.id === chatId);
    const chatTitle = chat?.title?.toLowerCase() || '';

    // 🟥 Hide Add to Space if *either* the route title or the backend title says "Document Check"
    if (
      title.toLowerCase().includes('document check') ||
      chatTitle.includes('document check')
    ) {
      return false;
    }

    const inChat =
      location.pathname.includes('/chat') &&
      chatId !== 'current' &&
      !isResearchSession;

    // const hasReport = Boolean(report);

    return inChat;
  };

  const getTitle = () => {
    if (location.pathname.includes('/chat')) return getChatTitle();
    if (location.pathname.includes('/document')) return 'Document Check';
    if (location.pathname.includes('/spaces')) return 'Spaces';
    if (location.pathname.includes('/history')) return 'Chat History';
    if (location.pathname.includes('/settings')) return 'Settings';
    if (location.pathname.includes('/research')) return 'Professional Research';
    if (location.pathname.includes('/researches'))
      return 'Professional Research Reports';
    return 'Legal Intelligence';
  };
  const handleClearMemory = async () => {
    const toastId = toast.loading('Clearing memory...');
    try {
      await clearMemory();
      toast.success('Memory cleared successfully!', { id: toastId });
    } catch (err) {
      toast.error('Failed to clear memory.', { id: toastId });
    }
  };
  const isLoadingTitle =
    chatId !== 'current' && !chatFromRedux && !chatFromLocal && loading; // only show loader if Redux is actually fetching
  const isLoadingChatMeta =
    chatId !== 'current' && loading && !chatFromRedux && !chatFromLocal;

  const formatTitleForMobile = (title) => {
    if (!isMobile) return title;

    if (!title) return '';

    // Hard limit for mobile header
    const MAX_LENGTH = 22;

    if (title.length <= MAX_LENGTH) return title;

    // Try shortening intelligently first
    const words = title.split(' ');

    if (words.length > 3) {
      return words.slice(0, 3).join(' ') + '…';
    }

    return title.slice(0, MAX_LENGTH) + '…';
  };

  return (
    <>
      <header
        className={`px-6 py-3 ${
          isDarkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } border-b shadow-sm flex items-center justify-between`}
      >
        {isMobile && (
          <button
            onClick={toggleSidebar}
            data-tooltip-id="header-tooltip"
            data-tooltip-content="Toggle Sidebar"
            data-tooltip-place="bottom"
          >
            <AlignJustify className="h-6 w-6 text-current" />
          </button>
        )}
        <div className="text-sm font-semibold truncate max-w-[220px] sm:max-w-[300px]">
          {isLoadingTitle ? (
            <span className="inline-block w-24 h-4 bg-gray-300 dark:bg-gray-700 rounded animate-pulse"></span>
          ) : (
            formatTitleForMobile(getTitle())
          )}
        </div>

        <div className="flex items-center space-x-3">
          {!isResearchSession && shouldShowAddToSpace() && (
            <>
              {(() => {
                if (isLoadingChatMeta) {
                  return (
                    <span className="inline-block w-20 h-6 rounded bg-gray-300 dark:bg-gray-700 animate-pulse" />
                  );
                }

                const chat = chatHistory.find((c) => c.id === chatId);
                const space = chat?.space;

                if (space) {
                  return (
                    <Link
                      to={`/app/spaces/${space.id}`}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium transition-colors duration-200 ${
                        isDarkMode
                          ? 'bg-indigo-900 text-indigo-300 hover:bg-indigo-800'
                          : 'bg-indigo-100 text-brand hover:bg-indigo-200'
                      }`}
                    >
                      <FolderOpen className="w-3.5 h-3.5" />
                      {space.name}
                    </Link>
                  );
                }

                return (
                  <button
                    onClick={() => setIsAddToSpaceOpen(true)}
                    data-tooltip-id="header-tooltip"
                    data-tooltip-content="Add this chat to a Space"
                    data-tooltip-place="bottom"
                    className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                      isDarkMode
                        ? 'bg-brandLight text-black'
                        : 'bg-brandLighter text-black hover:bg-gray-200'
                    }`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    <span className="sm:hidden">Add</span>
                    <span className="hidden sm:inline">Add to Space</span>
                  </button>
                );
              })()}
            </>
          )}
          <button
            onClick={toggleTheme}
            data-tooltip-id="header-tooltip"
            data-tooltip-content={
              isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'
            }
            data-tooltip-place="bottom"
            className={`p-2 rounded-full ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          {/* <button
            onClick={() => setShowConfirm(true)}
            className={`p-2 rounded-full ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
            title="Clear memory"
          >
            <RotateCcw size={20} />
          </button> */}
          {/* <button
            onClick={startTour}
            data-tooltip-id="header-tooltip"
            data-tooltip-content="Start Tour"
            data-tooltip-place="bottom"
            className={`p-2 rounded-full ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
          >
            <HelpCircle size={20} />
          </button> */}

          <button
            onClick={() => {
              setIsRightSidebarOpen(true);
              setHighlightDots(false);
            }}
            data-tooltip-id="header-tooltip"
            data-tooltip-content="Reports and History"
            data-tooltip-place="bottom"
            className={`relative p-2 rounded-full ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
          >
            {highlightDots && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-brand rounded-full animate-ping"></span>
            )}
            {highlightDots && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-brand rounded-full"></span>
            )}
            <MoreHorizontal size={20} />
          </button>
          {isChatRoute && hasActiveChat && resources?.length > 0 && (
            <button
              onClick={() => setIsResourcesSidebarOpen(true)}
              data-tooltip-id="header-tooltip"
              data-tooltip-content="View Sources"
              data-tooltip-place="bottom"
              className={`flex items-center gap-2 px-3 h-8 rounded-full border text-xs font-semibold transition-all
                ${
                  isDarkMode
                    ? 'border-gray-600 bg-gray-800 hover:bg-gray-700 text-gray-200'
                    : 'border-gray-200 bg-white hover:border-brand hover:text-brand text-gray-600 shadow-sm'
                }`}
            >
              <SidebarSquareIcon size={15} darkMode={isDarkMode} />
              <span className="hidden sm:inline tracking-wide">Sources</span>
            </button>
          )}
        </div>
      </header>

      <RightSidebar
        isOpen={isRightSidebarOpen}
        onClose={() => setIsRightSidebarOpen(false)}
        isDarkMode={isDarkMode}
        // onClearMemory={() => setShowConfirm(true)}
      ></RightSidebar>
      <ResourcesSidebar
        isOpen={isResourcesSidebarOpen}
        onClose={() => setIsResourcesSidebarOpen(false)}
        resources={resources}
        isDarkMode={isDarkMode}
      />
      <ClearMemoryDialog
        show={showConfirm}
        isDarkMode={isDarkMode}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleClearMemory}
      />
      <AddToSpaceModal
        isOpen={isAddToSpaceOpen}
        onClose={() => setIsAddToSpaceOpen(false)}
        chatId={chatId}
        uid={user?.id}
        isDarkMode={isDarkMode}
        refreshChatHistory={refreshChatHistory}
      />
      {!isMobile && (
        <Tooltip
          id="header-tooltip"
          delayShow={250}
          positionStrategy="fixed"
          className="!z-[9999] !text-xs !rounded-md !px-3 !py-1.5 !bg-gray-900 !text-white dark:!bg-brand !shadow-lg"
        />
      )}
    </>
  );
}
